import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_API_URL;

function FactoryDuserAuditDetails() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [checklistContent, setChecklistContent] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [subsectionQuestions, setSubsectionQuestions] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('dUser'));
  const FactoryCode = userData?.FactoryCode;
  const department = userData?.department;
  
  // Get checklist ID from URL params
  const { checklistId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (FactoryCode && department && checklistId) {
      fetchChecklistDetails();
    } else {
      setError("Missing required information");
      setLoading(false);
    }
  }, [FactoryCode, department, checklistId]);

  const fetchChecklistDetails = async () => {
    try {
      setLoading(true);
      
      // First fetch the checklist details
      const detailsResponse = await fetch(
        `${BASE_URL}/get-factory-department-assigned-checklists/${FactoryCode}/${department}`
      );

      if (!detailsResponse.ok) {
        throw new Error(`HTTP error! Status: ${detailsResponse.status}`);
      }

      const detailsData = await detailsResponse.json();

      if (detailsData.success && detailsData.checklists) {
        const checklist = detailsData.checklists.find(cl => cl.checklist_id === parseInt(checklistId));
        
        if (checklist) {
          setSelectedChecklist(checklist);
          // Now fetch the content
          await fetchChecklistContent(checklistId);
        } else {
          setError("Checklist not found");
        }
      } else {
        setError("Failed to fetch checklist details");
      }
    } catch (error) {
      console.error('Error fetching checklist details:', error);
      setError("Failed to fetch checklist details");
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklistContent = async (checklistId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-factory-department-checklist-content/${checklistId}/${FactoryCode}/${department}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setChecklistContent(data.content);
        
        // Fetch questions for this department
        const questionsResponse = await fetch(
          `${BASE_URL}/get-factory-department-questions/${checklistId}/${FactoryCode}/${department}`
        );

        if (!questionsResponse.ok) {
          throw new Error(`HTTP error! Status: ${questionsResponse.status}`);
        }

        const questionsData = await questionsResponse.json();

        if (questionsData.success) {
          setQuestions(questionsData.questions);
        }
      }
    } catch (error) {
      console.error('Error fetching checklist content:', error);
      setError("Failed to fetch checklist content");
    }
  };

  const fetchSubsectionQuestions = async (subsectionPath) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-factory-department-subsection-questions/${checklistId}/${FactoryCode}/${department}/${subsectionPath}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSubsectionQuestions(data.questions);
        setSelectedSubsection(data.subsection);
      }
    } catch (error) {
      console.error('Error fetching subsection questions:', error);
      setError("Failed to fetch subsection questions");
    }
  };

  const handleSubsectionClick = (subsection, path = '') => {
    const subsectionPath = path ? `${path}|${subsection.name}` : subsection.name;
    fetchSubsectionQuestions(subsectionPath);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'badge bg-warning text-dark';
      case 'in progress':
        return 'badge bg-info text-dark';
      case 'completed':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const renderQuestionDetails = (question) => {
    let answerOptions = [];
    let optionPoints = [];

    try {
      answerOptions = typeof question.answerOptions === 'string' 
        ? JSON.parse(question.answerOptions) 
        : question.answerOptions || [];
      
      optionPoints = typeof question.optionPoints === 'string'
        ? JSON.parse(question.optionPoints)
        : question.optionPoints || [];
    } catch (error) {
      console.error('Error parsing question options:', error);
    }

    return (
      <div key={question.id} className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary fw-bold">{question.questionText}</h6>
          <div className="question-details">
            <p className="mb-2"><strong>Answer Type:</strong> <span className="badge bg-light text-dark">{question.answerType}</span></p>
            {question.answerType !== 'FreeText' && answerOptions.length > 0 && (
              <div className="answer-options">
                <strong className="text-secondary">Answer Options:</strong>
                <ul className="list-group list-group-flush mt-2">
                  {answerOptions.map((option, index) => (
                    <li key={index} className="list-group-item py-1 px-0 border-0">
                      <i className="bi bi-circle-fill text-primary me-2" style={{ fontSize: '0.5rem' }}></i>
                      {option} {optionPoints[index] !== undefined && 
                        <span className="badge bg-secondary ms-2">{optionPoints[index]} points</span>
                      }
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSubsections = (subsections, path = '') => {
    return subsections.map((subsection, index) => {
      const sectionId = `${path}-${index}`;
      const isExpanded = expandedSections[sectionId];
      const hasQuestions = subsection.questions && subsection.questions.length > 0;
      const hasSubsections = subsection.subsections && subsection.subsections.length > 0;

      return (
        <div key={sectionId} className="card mb-2">
          <div 
            className="card-header bg-light cursor-pointer" 
            onClick={() => toggleSection(sectionId)}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 d-flex align-items-center">
                <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
                {subsection.name}
              </h6>
              <div className="d-flex gap-2">
                {hasQuestions && 
                  <span className="badge bg-primary">
                    <i className="bi bi-question-circle me-1"></i>
                    {subsection.questions.length}
                  </span>
                }
                {hasSubsections && 
                  <span className="badge bg-info">
                    <i className="bi bi-folder me-1"></i>
                    {subsection.subsections.length}
                  </span>
                }
              </div>
            </div>
          </div>
          
          {isExpanded && (
            <div className="card-body">
              {hasQuestions && (
                <button 
                  className="btn btn-outline-primary btn-sm mb-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubsectionClick(subsection, path);
                  }}
                >
                  <i className="bi bi-eye me-1"></i>
                  View Questions ({subsection.questions.length})
                </button>
              )}
              
              {hasSubsections && (
                <div className="nested-subsections ms-3">
                  {renderSubsections(subsection.subsections, `${path}|${subsection.name}`)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading audit details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => navigate('/factory-departmentuser')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="text-center mb-4">
            <h1 className="display-5 fw-bold text-primary mb-2">Audit Details</h1>
            <p className="lead text-muted">
              Department: <span className="fw-bold text-dark">{department}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Checklist Detail View */}
      <div className="row">
        <div className="col-12">
          {/* Detail Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center mb-3 mb-md-0">
              <button 
                className="btn btn-outline-secondary me-3"
                onClick={() => navigate('/factory-departmentuser')}
              >
                <i className="bi bi-arrow-left me-2"></i>Back to Dashboard
              </button>
              {selectedChecklist && (
                <div>
                  <h3 className="mb-1 text-primary">{selectedChecklist.checklist_name}</h3>
                  <span className={getStatusBadgeClass(selectedChecklist.status)}>
                    {selectedChecklist.status}
                  </span>
                </div>
              )}
            </div>
          </div>

          {checklistContent && (
            <div className="row">
              <div className="col-lg-8 col-md-12">
                {/* Department Section */}
                <div className="card mb-4">
                  <div className="card-header bg-primary text-white">
                    <h4 className="mb-0">
                      <i className="bi bi-diagram-3 me-2"></i>
                      Department: {checklistContent.name}
                    </h4>
                  </div>
                  <div className="card-body">
                    {/* Direct Questions */}
                    {checklistContent.questions && checklistContent.questions.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-secondary mb-3">
                          <i className="bi bi-question-circle me-2"></i>
                          Department Questions ({checklistContent.questions.length})
                        </h5>
                        <div className="questions-list">
                          {questions
                            .filter(q => checklistContent.questions.includes(q.id))
                            .map(question => renderQuestionDetails(question))}
                        </div>
                      </div>
                    )}

                    {/* Subsections */}
                    {checklistContent.subsections && checklistContent.subsections.length > 0 && (
                      <div>
                        <h5 className="text-secondary mb-3">
                          <i className="bi bi-folder me-2"></i>
                          Subsections
                        </h5>
                        {renderSubsections(checklistContent.subsections)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Selected Subsection Questions Sidebar */}
              {selectedSubsection && (
                <div className="col-lg-4 col-md-12">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <i className="bi bi-eye me-2"></i>
                        {selectedSubsection.name}
                      </h6>
                      <button 
                        className="btn btn-sm btn-outline-light"
                        onClick={() => {
                          setSelectedSubsection(null);
                          setSubsectionQuestions([]);
                        }}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                    <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      <div className="questions-list">
                        {subsectionQuestions.map(question => renderQuestionDetails(question))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FactoryDuserAuditDetails;